import { useClerk, useUser } from '@clerk/react'
import type { SessionResponse, WorkspaceSummary } from '@schemas/session'
import { WorkspaceRoleEnum } from '@schemas/workspace'
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { ApiRequestError, api } from '../api.ts'
import { landingPathForRole, workspaceCapabilityLine } from '../lib/can-manage.ts'
import { cn } from '../lib/cn.ts'
import { posthogCapture } from '../lib/posthog.ts'

export interface AccountMenuProps {
  session: SessionResponse
}

const SECTION_LABEL = 'px-4 pb-2 pt-3 font-mono text-[10px] uppercase tracking-[0.12em] text-ink-secondary'
const ROW = 'flex w-full items-center gap-3 px-4 py-2.5 text-left text-xs text-ink-soft hover:bg-ground-hover'

function RoleBadge({ role }: { role: WorkspaceSummary['role'] }) {
  const isAdmin = role === WorkspaceRoleEnum.enum.admin
  return (
    <span
      className={cn(
        'shrink-0 border px-1.5 py-1 font-mono text-[9px] font-medium uppercase tracking-[0.08em]',
        isAdmin
          ? 'border-signal-ok-border bg-signal-ok-bg text-signal-ok'
          : 'border-border-hairline-strong bg-ground-surface text-ink-secondary'
      )}
    >
      {role}
    </span>
  )
}

// RAL-121 rules 2 and 13: workspace and account switching live here, not in the sidebar —
// switching is a weeks-apart action and should not hold the shell's most valuable real estate.
// The menu also carries the one-off "Invite someone to <workspace>" row until the workspace has a
// team, at which point the Team nav item owns invitations and this row disappears.
export function AccountMenu({ session }: AccountMenuProps) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { user } = useUser()
  const clerk = useClerk()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Dismissing puts focus back on the trigger. Without it a keyboard user who presses Escape is
  // dropped at the top of the document, several tab stops from where they were (RAL-121 part 1
  // carry-over). Rows that navigate away close with setOpen alone: the destination owns focus.
  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, close])

  const menuItems = (): HTMLElement[] =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])

  // An opened menu focuses its first item, so the arrow keys below have somewhere to rove from.
  // Queried inline rather than through menuItems() so the effect depends on `open` alone.
  useEffect(() => {
    if (!open) return
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
  }, [open])

  // Roving focus per the menu pattern: Up/Down wrap, Home/End jump. Tab is left alone — it
  // leaves the menu, which is what a keyboard user expects from a dismissible popup.
  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const items = menuItems()
    if (items.length === 0) return
    const current = items.indexOf(document.activeElement as HTMLElement)
    const next = ((): number | null => {
      switch (event.key) {
        case 'ArrowDown':
          return (current + 1) % items.length
        case 'ArrowUp':
          return (current - 1 + items.length) % items.length
        case 'Home':
          return 0
        case 'End':
          return items.length - 1
        default:
          return null
      }
    })()
    if (next === null) return
    event.preventDefault()
    items[next]?.focus()
  }

  const { activeWorkspace, workspaces } = session
  const email = user?.primaryEmailAddress?.emailAddress ?? 'Signed in'
  // A solo creator belongs to one workspace and has nothing to switch between, so the switcher
  // section never renders for them at all (rule 12).
  const showSwitcher = workspaces.length > 1
  const showInvite = activeWorkspace.role === WorkspaceRoleEnum.enum.admin && !activeWorkspace.isTeam

  const switchWorkspace = async (workspace: WorkspaceSummary) => {
    setError(null)
    if (workspace.id === activeWorkspace.id) {
      setOpen(false)
      return
    }
    try {
      await api.post(`/api/workspaces/${workspace.id}/activate`)
      posthogCapture('workspace_switched')
      setOpen(false)
      // A hard navigation, not a client-side one: the switch changes the role, the accounts, the
      // campaign and the nav itself, so every loader and every cached query has to start again.
      // The role you land with decides where (rule 4) — demoted to member, you come out on
      // Published rather than a Queue you cannot act on.
      window.location.assign(landingPathForRole(workspace.role))
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.error.message : 'Failed to switch workspace')
    }
  }

  return (
    <div className="relative w-full">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        ref={triggerRef}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 border border-border-hairline-strong bg-ground-surface px-2.5 py-2 text-left hover:bg-ground-hover"
      >
        <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-ink-soft">{email}</span>
        <span aria-hidden className="shrink-0 font-mono text-[10px] text-ink-secondary">
          ▾
        </span>
      </button>

      {open ? (
        <>
          {/* Light dismiss. A transparent full-screen layer is the smallest thing that closes the
              menu on any outside click without a document-level listener racing the toggle. */}
          <button
            type="button"
            aria-label="Close account menu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={close}
          />
          <div
            role="menu"
            aria-label="Account"
            ref={menuRef}
            onKeyDown={onMenuKeyDown}
            className="absolute bottom-full left-0 z-50 mb-2 w-[272px] border border-border-hairline-strong bg-ground-canvas py-1 shadow-[9px_9px_0_rgba(0,0,0,0.45)]"
          >
            <div className="px-4 pb-2 pt-3">
              <p className="truncate font-mono text-[10px] text-ink-soft">{email}</p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.12em] text-ink-secondary">Signed in</p>
            </div>

            {showSwitcher ? (
              <div className="border-t border-border-hairline">
                <p className={SECTION_LABEL}>Switch workspace</p>
                {workspaces.map((workspace) => (
                  <button
                    key={workspace.id}
                    type="button"
                    role="menuitem"
                    onClick={() => void switchWorkspace(workspace)}
                    aria-current={workspace.id === activeWorkspace.id ? 'true' : undefined}
                    className={cn(
                      ROW,
                      'border-l-[3px]',
                      workspace.id === activeWorkspace.id
                        ? 'border-l-signal-oxide bg-ground-hover'
                        : 'border-l-transparent'
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-display text-[13px] font-bold uppercase text-ink-primary">
                        {workspace.name}
                      </span>
                      <span className="mt-1 block font-mono text-[10px] text-ink-secondary">
                        {workspaceCapabilityLine(workspace)}
                      </span>
                    </span>
                    <RoleBadge role={workspace.role} />
                  </button>
                ))}
              </div>
            ) : null}

            {session.accounts.length > 0 ? (
              <div className="border-t border-border-hairline">
                <p className={SECTION_LABEL}>Account in {activeWorkspace.name}</p>
                {session.accounts.map((account) => (
                  <p key={account.id} className="flex items-center gap-2.5 px-4 py-2 text-xs text-ink-soft">
                    <span
                      aria-hidden
                      className={cn(
                        'size-1.5 shrink-0',
                        account.id === session.accountId ? 'bg-signal-ok' : 'border border-border-hairline-strong'
                      )}
                    />
                    <span className="truncate font-mono text-[11px]">{account.displayName}</span>
                    {session.accounts.length === 1 ? (
                      <span className="ml-auto font-mono text-[10px] text-ink-secondary">only account</span>
                    ) : null}
                  </p>
                ))}
              </div>
            ) : null}

            <div className="border-t border-border-hairline py-1">
              {showInvite ? (
                // RAL-81 owns the invitation form and endpoint; this row points at the route it
                // will fill (part 2). Until then /team is the placeholder page.
                <Link role="menuitem" to="/team" className={ROW} onClick={() => setOpen(false)}>
                  Invite someone to {activeWorkspace.name}
                </Link>
              ) : null}
              <Link role="menuitem" to="/settings" className={ROW} onClick={() => setOpen(false)}>
                Settings
              </Link>
              <button type="button" role="menuitem" className={ROW} onClick={() => clerk.openUserProfile()}>
                Manage account
              </button>
              <Link role="menuitem" to="/privacy" className={ROW} onClick={() => setOpen(false)}>
                Privacy &amp; terms
              </Link>
              <button type="button" role="menuitem" className={ROW} onClick={() => void clerk.signOut()}>
                Sign out
              </button>
            </div>

            {error ? (
              <p className="px-4 pb-3 pt-1 font-mono text-[9px] uppercase tracking-[0.1em] text-signal-fail">{error}</p>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  )
}
